/**
 * Validation System for FreshCards Subsystem
 * 
 * This module provides comprehensive validation functionality for FreshCards data structures
 * and operations, integrating Zod schemas with the existing storage system.
 */

import { z } from 'zod';
import { 
  BlockSchema, 
  PageSchema, 
  BlockMetadataSchema, 
  PageMetadataSchema,
  StoragePayloadSchema,
  ImportPayloadSchema,
  CreatePageSchema,
  UpdatePageSchema,
  CreateBlockSchema,
  UpdateBlockSchema,
  ValidationResultSchema,
  ValidationErrorSchema,
  StorageErrorSchema,
  ImportErrorSchema,
  BackupErrorSchema,
  FreshCardsErrorSchema
} from './schemas';
import { Block, Page } from '../types/block';

// Validation result types
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  validatedItems: number;
  skippedItems: number;
  timestamp: Date;
}

export interface ValidationReport {
  overall: ValidationResult;
  pages: ValidationResult[];
  blocks: ValidationResult[];
  metadata: ValidationResult;
}

// Error types
export class ValidationError extends Error {
  constructor(
    public field: string,
    message: string,
    public value?: any,
    public code: string = 'VALIDATION_ERROR'
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class StorageError extends Error {
  constructor(
    public operation: string,
    message: string,
    public cause?: string
  ) {
    super(message);
    this.name = 'StorageError';
  }
}

export class ImportError extends Error {
  constructor(
    message: string,
    public validationErrors?: ValidationError[]
  ) {
    super(message);
    this.name = 'ImportError';
  }
}

export class BackupError extends Error {
  constructor(
    public operation: string,
    message: string,
    public backupId?: string
  ) {
    super(message);
    this.name = 'BackupError';
  }
}

// Main validation service
export class ValidationService {
  private static instance: ValidationService;

  static getInstance(): ValidationService {
    if (!ValidationService.instance) {
      ValidationService.instance = new ValidationService();
    }
    return ValidationService.instance;
  }

  // Validate a single page
  async validatePage(page: any): Promise<ValidationResult> {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      validatedItems: 0,
      skippedItems: 0,
      timestamp: new Date()
    };

    try {
      const validated = PageSchema.parse(page);
      result.validatedItems = 1;
      
      // Additional business logic validations
      if (validated.title.length > 100) {
        result.warnings.push('Title is very long (recommended under 100 characters)');
      }
      
      if (validated.children && validated.children.length > 50) {
        result.warnings.push('Page has many children (recommended under 50)');
      }

      // Check for circular references in children
      if (validated.children) {
        const hasCircularReference = this.checkCircularReferences(validated, page.id);
        if (hasCircularReference) {
          result.errors.push('Circular reference detected in page hierarchy');
          result.isValid = false;
        }
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        result.isValid = false;
        result.errors = error.errors.map((err: any) =>
          `${err.path.join('.')}: ${err.message}`
        );
      } else {
        result.isValid = false;
        result.errors.push(`Unexpected validation error: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return result;
  }

  // Validate multiple pages
  async validatePages(pages: Page[]): Promise<ValidationReport> {
    const overall: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      validatedItems: 0,
      skippedItems: 0,
      timestamp: new Date()
    };

    const pageResults: ValidationResult[] = [];

    for (const page of pages) {
      const result = await this.validatePage(page);
      pageResults.push(result);
      overall.validatedItems++;
      
      if (!result.isValid) {
        overall.isValid = false;
        overall.errors.push(...result.errors.map(err => `Page ${page.id}: ${err}`));
      }
      overall.warnings.push(...result.warnings.map(warn => `Page ${page.id}: ${warn}`));
    }

    return {
      overall,
      pages: pageResults,
      blocks: [], // Will be populated when validating blocks
      metadata: await this.validateMetadata(pages)
    };
  }

  // Validate a single block
  async validateBlock(block: any): Promise<ValidationResult> {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      validatedItems: 0,
      skippedItems: 0,
      timestamp: new Date()
    };

    try {
      const validated = BlockSchema.parse(block);
      result.validatedItems = 1;
      
      // Additional business logic validations
      if (validated.content && validated.content.length > 10000) {
        result.warnings.push('Block content is very long (recommended under 10,000 characters)');
      }
      
      if (validated.metadata.aiConfidence && (validated.metadata.aiConfidence < 0 || validated.metadata.aiConfidence > 1)) {
        result.errors.push('AI confidence must be between 0 and 1');
        result.isValid = false;
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        result.isValid = false;
        result.errors = error.errors.map((err: any) =>
          `${err.path.join('.')}: ${err.message}`
        );
      } else {
        result.isValid = false;
        result.errors.push(`Unexpected validation error: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return result;
  }

  // Validate multiple blocks
  async validateBlocks(blocks: Block[]): Promise<ValidationResult[]> {
    const results: ValidationResult[] = [];

    for (const block of blocks) {
      const result = await this.validateBlock(block);
      results.push(result);
    }

    return results;
  }

  // Validate metadata
  async validateMetadata(pages: Page[]): Promise<ValidationResult> {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      validatedItems: 1,
      skippedItems: 0,
      timestamp: new Date()
    };

    try {
      const now = new Date();
      
      for (const page of pages) {
        if (page.metadata.createdAt > now) {
          result.warnings.push(`Page ${page.id} has creation date in the future`);
        }
        
        if (page.metadata.updatedAt < page.metadata.createdAt) {
          result.errors.push(`Page ${page.id} has update date before creation date`);
          result.isValid = false;
        }
      }
    } catch (error) {
      result.isValid = false;
      result.errors.push(`Metadata validation error: ${error instanceof Error ? error.message : String(error)}`);
    }

    return result;
  }

  // Validate storage payload
  async validateStoragePayload(payload: any): Promise<ValidationResult> {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      validatedItems: 0,
      skippedItems: 0,
      timestamp: new Date()
    };

    try {
      const validated = StoragePayloadSchema.parse(payload);
      result.validatedItems = validated.pages.length + validated.blocks.length;
      
      // Check for duplicate IDs
      const allIds = new Set<string>();
      const duplicateIds = new Set<string>();
      
      [...validated.pages, ...validated.blocks].forEach(item => {
        if (allIds.has(item.id)) {
          duplicateIds.add(item.id);
        }
        allIds.add(item.id);
      });
      
      if (duplicateIds.size > 0) {
        result.errors.push(`Duplicate IDs found: ${Array.from(duplicateIds).join(', ')}`);
        result.isValid = false;
      }
      
    } catch (error) {
      if (error instanceof z.ZodError) {
        result.isValid = false;
        result.errors = error.errors.map((err: any) =>
          `${err.path.join('.')}: ${err.message}`
        );
      } else {
        result.isValid = false;
        result.errors.push(`Storage payload validation error: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return result;
  }

  // Validate import payload
  async validateImportPayload(payload: any): Promise<ValidationResult> {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      validatedItems: 0,
      skippedItems: 0,
      timestamp: new Date()
    };

    try {
      const validated = ImportPayloadSchema.parse(payload);
      result.validatedItems = 1;
      
      if (validated.pages) {
        const pageValidation = await this.validatePages(validated.pages);
        result.errors.push(...pageValidation.overall.errors.map(err => `Import: ${err}`));
        result.warnings.push(...pageValidation.overall.warnings.map(warn => `Import: ${warn}`));
        
        if (!pageValidation.overall.isValid) {
          result.isValid = false;
        }
      }
      
      if (validated.blocks) {
        const blockResults = await this.validateBlocks(validated.blocks);
        blockResults.forEach(blockResult => {
          if (!blockResult.isValid) {
            result.isValid = false;
            result.errors.push(...blockResult.errors.map(err => `Import: ${err}`));
          }
          result.warnings.push(...blockResult.warnings.map(warn => `Import: ${warn}`));
        });
      }
      
    } catch (error) {
      if (error instanceof z.ZodError) {
        result.isValid = false;
        result.errors = error.errors.map((err: any) =>
          `${err.path.join('.')}: ${err.message}`
        );
      } else {
        result.isValid = false;
        result.errors.push(`Import payload validation error: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return result;
  }

  // Check for circular references in page hierarchy
  private checkCircularReferences(page: Page, targetId: string, visited = new Set<string>()): boolean {
    if (visited.has(page.id)) {
      return page.id === targetId; // Circular reference found
    }
    
    visited.add(page.id);
    
    if (page.children) {
      for (const child of page.children) {
        if (this.checkCircularReferences(child, targetId, new Set(visited))) {
          return true;
        }
      }
    }
    
    return false;
  }

  // Validate form data
  validateCreatePage(data: any): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      validatedItems: 1,
      skippedItems: 0,
      timestamp: new Date()
    };

    try {
      CreatePageSchema.parse(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        result.isValid = false;
        result.errors = error.errors.map((err: any) =>
          `${err.path.join('.')}: ${err.message}`
        );
      } else {
        result.isValid = false;
        result.errors.push(`Create page validation error: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return result;
  }

  validateUpdatePage(data: any): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      validatedItems: 1,
      skippedItems: 0,
      timestamp: new Date()
    };

    try {
      UpdatePageSchema.parse(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        result.isValid = false;
        result.errors = error.errors.map((err: any) =>
          `${err.path.join('.')}: ${err.message}`
        );
      } else {
        result.isValid = false;
        result.errors.push(`Update page validation error: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return result;
  }

  validateCreateBlock(data: any): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      validatedItems: 1,
      skippedItems: 0,
      timestamp: new Date()
    };

    try {
      CreateBlockSchema.parse(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        result.isValid = false;
        result.errors = error.errors.map((err: any) =>
          `${err.path.join('.')}: ${err.message}`
        );
      } else {
        result.isValid = false;
        result.errors.push(`Create block validation error: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return result;
  }

  validateUpdateBlock(data: any): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      validatedItems: 1,
      skippedItems: 0,
      timestamp: new Date()
    };

    try {
      UpdateBlockSchema.parse(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        result.isValid = false;
        result.errors = error.errors.map((err: any) =>
          `${err.path.join('.')}: ${err.message}`
        );
      } else {
        result.isValid = false;
        result.errors.push(`Update block validation error: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    return result;
  }
}

// Export singleton instance and utilities
export const validationService = ValidationService.getInstance();

// Convenience functions for direct use
export const validatePage = (page: any) => validationService.validatePage(page);
export const validatePages = (pages: Page[]) => validationService.validatePages(pages);
export const validateBlock = (block: any) => validationService.validateBlock(block);
export const validateBlocks = (blocks: Block[]) => validationService.validateBlocks(blocks);
export const validateStoragePayload = (payload: any) => validationService.validateStoragePayload(payload);
export const validateImportPayload = (payload: any) => validationService.validateImportPayload(payload);

// Form validation utilities
export const validateCreatePageData = (data: any) => validationService.validateCreatePage(data);
export const validateUpdatePageData = (data: any) => validationService.validateUpdatePage(data);
export const validateCreateBlockData = (data: any) => validationService.validateCreateBlock(data);
export const validateUpdateBlockData = (data: any) => validationService.validateUpdateBlock(data);